import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import type { CycleEvaluationFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationFormNavigator';
import type { ReviewTemplate } from '@/types/review/ReviewTemplate';

import {
    useGetCycleEvaluation,
    useUpdateCycleEvaluation,
} from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useImportTemplateInCycleQuestionnaire } from '@/api/cycle-evaluation-services/cycle-questionnaire';
import {
    useAmelioReviewTemplates,
    useReviewTemplates,
} from '@/api/review-services/review-template';
import { selectTemplateOnNextAtom } from '@/atoms/cycleEvaluation';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleEvaluationSteps } from '@/constants/cycle-evaluation/CycleEvaluationSteps';
import { ReviewTemplateAccessType } from '@/constants/review/ReviewTemplateAccessType';
import { ReviewTemplateStatus } from '@/constants/review/ReviewTemplateStatus';
import { useAmelioTranslations, useNavigate, useRegisterPageAction } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useRevieweesFromAudience } from '@/hooks/cycle-instance/useRevieweesFromAudience';
import { useAssignTemplate } from '@/hooks/template/useAssignTemplate';
import { useSelectTemplateNavigation } from '@/hooks/template/useSelectTemplateNavigation';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import { SelectTemplate } from '@/pages/shared/SelectTemplate/SelectTemplate';
import { showWarningToast } from '@/plugins/Toast';
import { getCycleEvaluationStepPath } from '@/utils/cycleEvaluation';

const INITIAL_TEMPLATES_PAGE_SIZE = 5;
const SUBSEQUENT_TEMPLATES_PAGE_SIZE = 6;

export const SelectTemplatePage = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const navigate = useNavigate();
    const { cycleEvaluationId } = useParams<CycleEvaluationFormNavigatorParams>();

    const [selectedTemplate, setSelectedTemplate] = useState<ReviewTemplate>();

    const {
        data: cycleEvaluation,
        isLoading: isFetchingCycleEvaluation,
        isError: isFetchingCycleEvaluationError,
    } = useGetCycleEvaluation(cycleEvaluationId);

    const { cycleQuestionnaire } = cycleEvaluation || {};

    const {
        data: paginatedTemplates,
        fetchNextPage: fetchMoreTemplates,
        hasNextPage: hasMoreTemplates,
        isLoading: isFetchingTemplates,
        isFetchingNextPage: isFetchingNextPage,
        isError: isFetchingTemplatesError,
    } = useReviewTemplates({
        pageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        initialPageSize: INITIAL_TEMPLATES_PAGE_SIZE,
        subsequentPageSize: SUBSEQUENT_TEMPLATES_PAGE_SIZE,
        meta: {
            reviewType: cycleEvaluation?.reviewType,
        },
        options: {
            enabled: !!cycleEvaluation,
        },
    });

    const { data: amelioTemplates } = useAmelioReviewTemplates(cycleEvaluation?.reviewType, {
        enabled: !!cycleEvaluation,
    });

    const { mutateAsync: importTemplateInCycleQuestionnaire } =
        useImportTemplateInCycleQuestionnaire();

    const { mutateAsync: updateCycleEvaluation } = useUpdateCycleEvaluation();

    const reviewees = useRevieweesFromAudience({ cycleEvaluation });
    const previewEvaluee = reviewees[0];

    const templates = useMemo(
        () => [
            ...(paginatedTemplates?.pages.flatMap((page) => page.items) ?? []),
            ...(amelioTemplates ?? []),
        ],
        [paginatedTemplates, amelioTemplates],
    );

    const displayedTemplates: ReviewTemplate[] = [
        {
            id: '',
            title: { en: t('evaluation360SelectTemplate.startFromScratch.title') },
            description: t('evaluation360SelectTemplate.startFromScratch.description'),
            reviewType: EvaluationType.EVALUATION_360,
            status: ReviewTemplateStatus.DRAFT,
            accessType: ReviewTemplateAccessType.APP_WIDE,
            reviewQuestionnaires: [],
            cycleQuestionnaires: [],
            questions: [],
        },
        ...templates,
    ];

    const onManageTemplate = () => {
        navigate({
            path: `${RouteName.ManageTemplates}?cycleEvaluationId=${cycleEvaluationId}`,
        });
    };

    const onSelectTemplate = (template: ReviewTemplate) => {
        if (selectedTemplate?.id === template?.id) {
            setSelectedTemplate(undefined);
        } else {
            setSelectedTemplate(template);
        }
    };

    const onUseTemplate = useCallback(
        async (template: ReviewTemplate) => {
            if (!template?.id) {
                showWarningToast({
                    message: t('evaluation360SelectTemplate.validation.templateValidation'),
                });
                return;
            }

            if (!cycleQuestionnaire) {
                handleError({});
                return;
            }

            try {
                setSelectedTemplate(template);

                await importTemplateInCycleQuestionnaire({
                    questionnaireId: cycleQuestionnaire.id,
                    templateId: template.id,
                });

                navigate({
                    path: `${RouteName.CycleEvaluationForm}/${getCycleEvaluationStepPath(CycleEvaluationSteps.QUESTIONNAIRE_CREATION)}/${cycleEvaluationId}`,
                });
            } catch (error) {
                handleError({ error });
            }
        },
        [
            cycleQuestionnaire,
            cycleEvaluationId,
            importTemplateInCycleQuestionnaire,
            handleError,
            navigate,
            t,
        ],
    );

    const questionnaireCreationPath = `${RouteName.CycleEvaluationForm}/${getCycleEvaluationStepPath(CycleEvaluationSteps.QUESTIONNAIRE_CREATION)}/${cycleEvaluationId}`;

    const updateSelectTemplateStep = useCallback(async () => {
        if (!cycleEvaluation) {
            handleError({});
            return false;
        }

        try {
            await updateCycleEvaluation?.({
                ...cycleEvaluation,
                currentStep: CycleEvaluationSteps.QUESTIONNAIRE_CREATION,
            });
        } catch (error) {
            handleError({ error });
            return false;
        }
        return true;
    }, [cycleEvaluation, updateCycleEvaluation, handleError]);

    const { onNext } = useSelectTemplateNavigation({
        selectedTemplate,
        questionnaireId: cycleQuestionnaire?.id,
        hasExistingQuestions: (cycleQuestionnaire?.reviewQuestions?.length ?? 0) > 0,
        navigationPath: questionnaireCreationPath,
        importTemplate: importTemplateInCycleQuestionnaire,
        updateSelectTemplateStep,
    });

    useAssignTemplate({
        reviewTemplateId:
            cycleQuestionnaire?.reviewTemplate?.id ||
            cycleQuestionnaire?.reviewTemplateId ||
            undefined,
        templates,
        setSelectedTemplate,
    });

    useRegisterPageAction(selectTemplateOnNextAtom, onNext);

    useFetchErrorsHandler({
        errors: [isFetchingTemplatesError, isFetchingCycleEvaluationError],
    });

    return (
        <SelectTemplate
            isSelfEvaluation={cycleEvaluation?.reviewType === EvaluationType.SELF_EVALUATION}
            templates={displayedTemplates}
            hasMoreTemplates={hasMoreTemplates}
            isFetchingTemplates={isFetchingTemplates || isFetchingCycleEvaluation}
            isFetchingNextPage={isFetchingNextPage}
            selectedTemplate={selectedTemplate}
            initialTemplateId={
                cycleQuestionnaire?.reviewTemplate?.id ||
                cycleQuestionnaire?.reviewTemplateId ||
                undefined
            }
            shouldAllowTemplateManagement={true}
            onManageTemplate={onManageTemplate}
            onSelectTemplate={onSelectTemplate}
            onUseTemplate={onUseTemplate}
            loadMore={fetchMoreTemplates}
            onStartBuilding={() => onNext(true)}
            previewEvaluee={previewEvaluee}
        />
    );
};
